/**
 * AddUserModal — Leadership-only "invite user" dialog.
 *
 * Collects full name, email and role, then POSTs via useCreateUser (Cognito sends the invite +
 * temporary-password email). Shows a success toast and closes on success.
 */
import { type FormEvent, useState } from 'react';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

import { useCreateUser } from '@/hooks/useUsers';
import type { DeliverProUserRole } from '@/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Select } from '@/components/ui/select';

const ROLES: DeliverProUserRole[] = ['PM', 'SA', 'Leadership'];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function AddUserModal({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): JSX.Element {
  const createUser = useCreateUser();

  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<DeliverProUserRole>('PM');

  const reset = (): void => {
    setFullName('');
    setEmail('');
    setRole('PM');
  };

  const handleOpenChange = (next: boolean): void => {
    if (!next) reset();
    onOpenChange(next);
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    if (!fullName.trim()) {
      toast.error('Full name is required');
      return;
    }
    if (!EMAIL_RE.test(email.trim())) {
      toast.error('A valid email is required');
      return;
    }
    createUser.mutate(
      { full_name: fullName.trim(), email: email.trim(), role },
      {
        onSuccess: () => {
          toast.success(`Invitation sent to ${email.trim()}`);
          reset();
          onOpenChange(false);
        },
        onError: (err) => {
          toast.error(err instanceof Error ? err.message : 'Failed to create user');
        },
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Invite user</DialogTitle>
          <DialogDescription>
            Invite a new user. They receive an email with a temporary password and appear as
            &ldquo;Invited&rdquo; until they sign in and set their own.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 py-1">
          <div className="space-y-1.5">
            <Label htmlFor="add-user-name">Full Name</Label>
            <Input
              id="add-user-name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Jane Doe"
              required
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="add-user-email">Email</Label>
            <Input
              id="add-user-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="jane.doe@example.com"
              required
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="add-user-role">Role</Label>
            <Select
              id="add-user-role"
              aria-label="Role"
              value={role}
              onChange={(v) => setRole(v as DeliverProUserRole)}
              options={ROLES.map((r) => ({ value: r, label: r }))}
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={createUser.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={createUser.isPending}>
              {createUser.isPending && <Loader2 className="animate-spin" aria-hidden="true" />}
              Send invite
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default AddUserModal;
